import React, { useState, ChangeEvent, useEffect } from 'react';
import { ModalWithButtons } from '../../ModalWithButtons/ModalWithButtons.component';
import { Grid, TextField, FormHelperTextProps } from '@material-ui/core';
import { useStyles } from './RoleEditor.styles';
import { LeavePrompt } from './../../Prompt/LeavePrompt/LeavePrompt.component';
import { verifyDuplicateRoleName } from '../../../services/rolesAndPermissions/rolesAndPermissions.service';
import { ValidationRow } from '../../ValidationRow/ValidationRow.components';

const NAME_MAX_LENGTH = 256;
const DESCRIPTION_MAX_LENGTH = 1024;

interface RoleEditorProps {
  title: string;
  onCancel: () => void;
  onSave: (roleId: string, roleName: string, roleDescription: string) => void;
  onChange: () => void;
  roleName: string;
  roleDescription: string;
  roleId: string;
}

export const RoleEditor: React.FC<RoleEditorProps> = ({
  title,
  onCancel,
  onSave,
  roleName: roleNameProp,
  roleDescription: roleDescriptionProp,
  roleId: roleIdProp,
}) => {
  const classes = useStyles();
  const [isLeavePromptOpen, setIsLeavePromptOpen] = useState<boolean>(false);
  const [roleName, setRoleName] = useState<string>(roleNameProp ?? '');
  const [roleDescription, setRoleDescription] = useState<string>(roleDescriptionProp ?? '');
  const [hasNameFocus, setHasNameFocus] = useState<boolean>(false);
  const [hasDescriptionFocus, setHasDescriptionFocus] = useState<boolean>(false);
  const [isUniqueRoleName, setUniqueRoleName] = useState<boolean>(false);
  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);

  const uniqueRoleNameValidation = () => {
    if (roleName === roleNameProp && roleName.length === 0) {
      setUniqueRoleName(false);
    } else if (roleName && roleName.length && roleName !== roleNameProp) {
      verifyDuplicateRoleName(roleName).then((result) => {
        setUniqueRoleName(!result);
      });
    }
  };

  const handleNameBlur = () => {
    uniqueRoleNameValidation();
  };

  const handleRoleNameChange = (value: string) => {
    setRoleName(value);
  };

  const handleRoleDecriptionChange = (value: string) => {
    setRoleDescription(value);
  };

  useEffect(() => {
    setIsDataChanged(roleNameProp !== roleName || roleDescription !== roleDescriptionProp);
  }, [roleNameProp, roleName, roleDescription, roleDescriptionProp]);

  const isRoleNameToLong = roleName.length > NAME_MAX_LENGTH;
  const isRoleDescriptionToLong = roleDescription.length > DESCRIPTION_MAX_LENGTH;

  const handleCancel = () => {
    isDataChanged ? setIsLeavePromptOpen(true) : onCancel();
  };

  return (
    <>
      <ModalWithButtons
        xCloseButton={handleCancel}
        open={true}
        size="xs"
        title={title}
        autoHeight
        onCancel={handleCancel}
        onConfirm={() => onSave(roleIdProp, roleName, roleDescription)}
        confirmButtonDisabled={
          !roleName ||
          !roleDescription ||
          isRoleNameToLong ||
          isRoleDescriptionToLong ||
          isUniqueRoleName ||
          !isDataChanged
        }
        handleClose={() => setIsLeavePromptOpen(true)}
      >
        <Grid container>
          <Grid item xs={12} className={classes.item}>
            <TextField
              id='nazwa-roli'
              value={roleName}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleRoleNameChange(e.target.value)}
              label="Nazwa roli*"
              aria-labelledby='nazwa-roli'
              variant="outlined"
              fullWidth
              onFocus={() => setHasNameFocus(true)}
              onBlur={() => handleNameBlur()}
              error={isRoleNameToLong || isUniqueRoleName}
              FormHelperTextProps={
                {
                  component: 'div',
                } as Partial<FormHelperTextProps<'p'>>
              }
              helperText={
                <ValidationRow
                  helperText={
                    isRoleNameToLong
                      ? 'Podana nazwa jest za długa'
                      : isUniqueRoleName
                      ? 'Podana nazwa już istnieje w systemie'
                      : ''
                  }
                  limitsVisible={hasNameFocus || isRoleNameToLong || isUniqueRoleName}
                  valueLength={roleName.length}
                  limit={NAME_MAX_LENGTH}
                  withError={isRoleNameToLong || isUniqueRoleName}
                />
              }
            />
          </Grid>
          <Grid item xs={12} className={classes.item}>
            <TextField
              id='opis-roli'
              value={roleDescription}
              multiline
              rows={5}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleRoleDecriptionChange(e.target.value)}
              label="Opis roli*"
              aria-labelledby='opis-roli'
              variant="outlined"
              fullWidth
              inputProps={{
                style: {
                  height: '100px',
                },
              }}
              onFocus={() => setHasDescriptionFocus(true)}
              onBlur={() => setHasDescriptionFocus(false)}
              error={isRoleDescriptionToLong}
              FormHelperTextProps={
                {
                  component: 'div',
                } as Partial<FormHelperTextProps<'p'>>
              }
              helperText={
                <ValidationRow
                  helperText={isRoleDescriptionToLong ? 'Podany opis jest za długi' : ''}
                  limitsVisible={hasDescriptionFocus || isRoleDescriptionToLong}
                  valueLength={roleDescription.length}
                  limit={DESCRIPTION_MAX_LENGTH}
                  withError={isRoleDescriptionToLong}
                />
              }
            />
          </Grid>
        </Grid>
      </ModalWithButtons>
      <LeavePrompt isOpen={isLeavePromptOpen} onConfirm={onCancel} onCancel={() => setIsLeavePromptOpen(false)} />
    </>
  );
};
